/**
 * Pass D — Headless Print Preview Page
 *
 * Route: /print-preview/:clientId
 * Query params:
 *   ?package=current|package|custom (default: package)
 *   ?report=prepared|compiled (default: prepared)
 *   ?section=portrait|landscape|all (default: all)
 *   ?token=<render-token>
 *   ?engagement_override=prepared|compiled (optional — override the
 *      client's stored engagement_type for the render only; used by
 *      the staff "preview as compiled/prepared" workflow)
 *
 * Consumed by the Playwright subprocess spawned by POST /api/pdf/generate.
 * Validates the render token, swaps in the backend-issued short-lived
 * service JWT (so subsequent /api/qbo/* /api/mappings/* calls succeed
 * without a user session), fetches the engagement data, then mounts
 * PrintModal in headless mode to render the package directly to the
 * document. Body class .pdf-server.section-* activates CSS overrides
 * (native T12 landscape, full dollar amounts, font bumps).
 *
 * When the package is fully rendered, sets body[data-render-ready=1] so
 * the Playwright caller can wait for it before calling page.pdf().
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PrintModal from '../components/PrintModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const consumeRenderToken = (token) =>
  axios.post(`${API_URL}/pdf/render-token/consume`, { token });

// Bare axios calls with explicit Authorization header — deliberately
// NOT the shared `api` instance, because its 401 interceptor redirects
// to /login which would derail the Playwright run.
const auth = (jwt) => ({ headers: { Authorization: `Bearer ${jwt}` } });
const getClient   = (jwt, id) => axios.get(`${API_URL}/clients/${id}`, auth(jwt));
// V21 — period_end forwarded so the cache key matches the pre-warm's.
const getAccounts = (jwt, id, periodEnd) => axios.get(
  `${API_URL}/qbo/accounts/${id}${periodEnd ? `?period_end=${encodeURIComponent(periodEnd)}` : ''}`,
  auth(jwt),
);
const getMapping  = (jwt, id) => axios.get(`${API_URL}/mappings/${id}`, auth(jwt));
const getTtm      = (jwt, id, endDate) => axios.get(
  `${API_URL}/qbo/ttm/${id}${endDate ? `?end_date=${encodeURIComponent(endDate)}` : ''}`,
  auth(jwt),
);

export default function PrintPreviewPage() {
  const { clientId } = useParams();
  const [search] = useSearchParams();
  const pkg = search.get('package') || 'package';
  const reportType = search.get('report') || 'prepared';
  const section = search.get('section') || 'all';
  const token = search.get('token') || '';
  const engagementOverride = search.get('engagement_override') || null;  // 'prepared' | 'compiled'

  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [ttmData, setTtmData] = useState(null);
  const [qboMeta, setQboMeta] = useState(null);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setError('Missing render token.');
      return;
    }
    let cancelled = false;

    // Body class controls CSS overrides for the server-PDF path.
    // V24 — also apply the compact `.mockup-print` typography so the
    // headless Export/Print PDF matches the on-screen preview exactly
    // (single source of truth). Fixes the whole-package format regression
    // where the .pdf-server path lacked the compact statement styling.
    document.body.classList.add('pdf-server', 'mockup-print');
    document.body.classList.add(`section-${section}`);
    // Day-3 cutover — mockup is now canonical print style. Apply the
    // class here (in addition to PrintModal's own mount effect) so the
    // mockup CSS resolves on the very first paint, before data loads.
    // This eliminates a one-frame layout flash where the compilation
    // report briefly rendered with default rules — which on some runs
    // caused the signature block to spill onto a phantom page 2.
    document.body.classList.add('mockup-print');
    document.body.removeAttribute('data-render-ready');

    // V18 Step 3 — Strip the Emergent platform badge from the headless
    // render. The /print-preview route is served from the React SPA's
    // index.html, which loads https://assets.emergent.sh/scripts/
    // emergent-main.js. That script appends a fixed-position
    // "<a id='emergent-badge'>Made with Emergent</a>" element. Playwright
    // captures the badge in the PDF, stamping it over every page
    // (including T12 data). Client-deliverable output cannot ship with
    // the platform badge.
    //
    // Strategy: poll-remove the badge for the first 2s in case the
    // injecting script runs after this effect. Inject a CSS rule that
    // hides any badge that re-appears later (e.g. mutated by the
    // platform script). Both are idempotent.
    const hideBadgeStyle = document.createElement('style');
    hideBadgeStyle.id = 'pdf-server-hide-emergent-badge';
    hideBadgeStyle.textContent = '#emergent-badge { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(hideBadgeStyle);
    const dropBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    dropBadge();
    const badgeInterval = setInterval(dropBadge, 200);
    setTimeout(() => clearInterval(badgeInterval), 2500);

    (async () => {
      try {
        // 1) Consume the render token; receive a scoped service JWT.
        const tokResp = await consumeRenderToken(token);
        const { service_jwt, client_id: scopedClientId, period_end: scopedPeriodEnd } = tokResp.data;
        if (scopedClientId !== clientId) {
          throw new Error('Token scope mismatch.');
        }

        // V21 (2026-06-11) — Per-section data gating.
        //
        // Previously TTM was fetched UNCONDITIONALLY for every section
        // (cover/portrait/landscape) — so a single 3-section package
        // render fired 3 × 12 = 36 QBO ProfitAndLoss calls just to
        // produce the T12 numbers needed by ONE section. With QBO's
        // ~300-call/hr rate-limit, batching three packages back-to-back
        // would trip the limit and surface as a NEW intermittent
        // failure once the page_goto/networkidle race was killed.
        //
        // Fix: only fire getTtm when section === 'landscape'. Cover
        // and portrait sections never read ttmData (verified: the
        // T12 component is the sole consumer). This drops the QBO
        // call count from 36 to 12 per full package render.
        //
        // CRITICAL — period_end MUST be forwarded from the consumed
        // token's scope so the React XHR hits the SAME cache key the
        // pre-warm populated. Without this, the React XHR omits
        // end_date and the handler defaults to "last completed month"
        // — a different key than the pre-warm wrote, double-firing
        // the QBO call.
        const needsTtm = section === 'landscape';
        const ttmPromise = needsTtm
          ? getTtm(service_jwt, clientId, scopedPeriodEnd).catch((e) => {
              console.error('ttm', e?.response?.status, e?.message);
              return null;
            })
          : Promise.resolve(null);

        // 2) Fetch the data each section actually needs. Cover +
        //    portrait skip the TTM endpoint entirely. Accounts is also
        //    sent with period_end so the cache-key match holds there
        //    too.
        const [clientResp, accountsResp, mappingResp, ttmResp] = await Promise.all([
          getClient(service_jwt, clientId).catch((e) => { console.error('client', e?.response?.status, e?.message); return null; }),
          getAccounts(service_jwt, clientId, scopedPeriodEnd).catch((e) => { console.error('accounts', e?.response?.status, e?.message); return null; }),
          getMapping(service_jwt, clientId).catch((e) => { console.error('mapping', e?.response?.status, e?.message); return null; }),
          ttmPromise,
        ]);

        if (cancelled) return;
        if (!clientResp || !accountsResp) {
          throw new Error('Failed to load engagement data.');
        }
        setClient(clientResp.data);
        setAccounts(accountsResp.data.accounts || []);
        setQboMeta({
          reconciliation: accountsResp.data.reconciliation,
          completeness:   accountsResp.data.completeness,
          period_start:   accountsResp.data.period_start,
          period_end:     accountsResp.data.period_end,
          period_start_eve: accountsResp.data.period_start_eve,
          ni_at_period_start: accountsResp.data.ni_at_period_start,
          cash_at_period_start: accountsResp.data.cash_at_period_start,
        });
        if (mappingResp?.data?.mapping) setMapping(mappingResp.data.mapping);
        if (ttmResp?.data) setTtmData(ttmResp.data);
      } catch (e) {
        console.error('PrintPreview load failed:', e);
        if (!cancelled) setError(e.message || String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [clientId, token, section]);

  // V21 (2026-06-11) — Section-strict readiness signal.
  //
  // Previously this fired when `client && accounts.length > 0 && mapping`
  // were loaded, ignoring `section` and `ttmData`. The Playwright caller
  // then separately awaited document.fonts.ready, which only worked
  // because page.goto(waitUntil:'networkidle') also blocked on font
  // requests. With networkidle removed (per V21 architectural fix), the
  // single readiness signal must encompass:
  //   (a) the data the current section actually NEEDS (cover/portrait
  //       don't need TTM; landscape does), AND
  //   (b) document.fonts.ready resolution, so layout has settled on
  //       final font metrics BEFORE the Playwright caller proceeds.
  // The Node renderSection() now relies on this single signal — no
  // separate fonts.ready wait, no 200ms fixed-sleep breathing room.
  useEffect(() => {
    const needsTtm = section === 'landscape';
    const dataReady =
      !!client && accounts.length > 0 && !!mapping &&
      (!needsTtm || !!ttmData);
    if (!dataReady) return;

    let cancelled = false;
    (async () => {
      // Wait for fonts to fully load BEFORE flipping the signal so
      // page.pdf() captures the DOM with final font metrics. Same
      // intent as the previous Node-side wait, just folded in.
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch (_) { /* ignore */ }
      }
      if (cancelled) return;
      // Two requestAnimationFrame nests so React's commit phase + any
      // layout-effects fire before the signal flips. Matches V19 behavior.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!cancelled) {
          document.body.setAttribute('data-render-ready', '1');
        }
      }));
    })();

    return () => { cancelled = true; };
  }, [client, accounts, mapping, ttmData, section]);

  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: 'monospace' }}>
        <h2>Print Preview Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!client || !mapping || accounts.length === 0) {
    return (
      <div style={{ padding: 20, fontSize: 12, color: '#666' }}>
        Loading engagement data…
      </div>
    );
  }

  return (
    <div ref={rootRef} data-testid="print-preview-root">
      <PrintModal
        isOpen={true}
        onClose={() => {}}
        accounts={accounts}
        client={engagementOverride
          ? { ...client, engagement_type: engagementOverride === 'compiled' ? 'compilation' : 'preparation' }
          : client}
        currentStatement="pl"
        ttmData={ttmData}
        mapping={mapping}
        qboMeta={qboMeta}
        userRole="staff"
        headless={true}
        section={section}
        forcePackage={pkg}
        forceReportType={reportType}
      />
    </div>
  );
}
